import { DN_STORE_BRANCH_MAP, STORE_BRANCH_MAP } from "./storeMaps";

/** Same token as the API: "PG Panormou" and "PG-ATH-PANORMOU" both become "panormou". */
export function storeMatchKey(name) {
  let text = String(name || "").trim().toLowerCase();
  text = text.replace(/^(pg|dn|unit|hub)[\s-]+/, "");
  text = text.replace(/^ath[\s-]+/, "");
  return text.replace(/[\s_-]+/g, "");
}

function brandHint(name) {
  const text = String(name || "").trim().toLowerCase();
  if (/^(pg|protein garden)([\s-]|$)/.test(text)) return "pg";
  if (/^(dn|dinas)([\s-]|$)/.test(text)) return "dn";
  if (/^hub([\s-]|$)/.test(text)) return "hub";
  return "";
}

function aliasLabels(label) {
  const key = String(label || "").trim();
  if (!key) return [];
  const out = [key];
  for (const map of [STORE_BRANCH_MAP, DN_STORE_BRANCH_MAP]) {
    if (map[key]) out.push(map[key]);
    Object.entries(map).forEach(([storeId, branchName]) => {
      if (branchName === key) out.push(storeId);
    });
  }
  return out;
}

/**
 * Catalog row for a session or Foxline store label.
 * Exact `location_name` wins. Otherwise match the street token and brand
 * so "PG-ATH-PANORMOU" resolves to "PG Panormou" and not "DN Panormou".
 */
export function storeRecordForLabel(label, storeRecords) {
  const records = storeRecords || [];
  const candidates = aliasLabels(label);
  for (let i = 0; i < candidates.length; i += 1) {
    const name = String(candidates[i] || "").trim();
    if (!name) continue;
    const exact = records.find((row) => String(row?.location_name || "").trim() === name);
    if (exact) return exact;
  }
  const needles = [...new Set(candidates.map(storeMatchKey).filter(Boolean))];
  if (!needles.length) return null;
  const hint = candidates.map(brandHint).find(Boolean) || "";
  let hits = records.filter((row) => needles.includes(storeMatchKey(row?.location_name)));
  if (hint) {
    const branded = hits.filter((row) => {
      const key = String(row?.brand_key || "").trim().toLowerCase();
      if (key) return key === hint;
      return brandHint(row?.location_name) === hint;
    });
    if (branded.length) hits = branded;
  }
  return hits.length === 1 ? hits[0] : null;
}

/** Warehouse id for a catalog store name. Null until `/store-locations` has that row. */
export function locationIdForStore(store, storeRecords) {
  const rec = storeRecordForLabel(store, storeRecords);
  const id = rec?.unified_location_id;
  if (id == null || id === "") return null;
  const num = Number(id);
  return Number.isFinite(num) ? num : null;
}

export function storeNameForLocationId(locationId, storeRecords) {
  if (locationId == null || locationId === "") return "";
  const id = Number(locationId);
  const rec = (storeRecords || []).find((r) => Number(r.unified_location_id) === id);
  return rec?.location_name ? String(rec.location_name).trim() : "";
}

export function locationScopeQuery(unifiedLocationId) {
  if (unifiedLocationId == null || unifiedLocationId === "") return "";
  return `unified_location_id=${encodeURIComponent(unifiedLocationId)}`;
}

/** Stores a T1 (or employee) may use from the session. Empty → no location lock. */
export function allowedStoresForUser(user) {
  const role = String(user?.role || "").trim().toLowerCase();
  if (!["store_manager", "manager", "employee", "crew", "chef"].includes(role)) {
    return null;
  }
  const fromList = Array.isArray(user?.stores)
    ? user.stores.map((s) => String(s || "").trim()).filter(Boolean)
    : [];
  if (fromList.length) return [...new Set(fromList)];
  const home = String(user?.store || "").trim();
  if (home && home !== "All Stores") return [home];
  return [];
}

/** True when the user is locked to exactly one store (classic single-store T1). */
export function isSingleStoreLocked(user) {
  const allowed = allowedStoresForUser(user);
  return Array.isArray(allowed) && allowed.length === 1;
}
